import { CalculationErrorChip } from '@monorepo/shared/componentsV2/errorDisplays/CalculationErrorChip';
import { Link } from '@monorepo/shared/componentsV2/Link';
import { ActionMenu } from '@monorepo/shared/componentsV2/menus/ActionMenu';
import { SyncingToAnalyticsIndicator } from '@monorepo/shared/componentsV2/SyncingToAnalyticsIndicator';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { useHasOrgUpdatePermissionOrSuperAdmin } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import { cardBorder } from '@monorepo/shared/styles/card';
import { bodyMedium, bodySmall } from '@monorepo/shared/styles/text';
import { WorkflowRefResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useWorkflowDelete } from '../../hooks/useWorkflowDelete';
import { useWorkflowsSyncingToAnalytics } from '../../hooks/useWorkflowsSyncingToAnalytics';
import { WorkflowStepsDiagram } from '../EditWorkflowStepsPage/WorkflowStepsDiagram';
import { RouteEditMode } from '../types';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: 51rem;
  padding: 1.2rem 1rem 1rem;
  ${cardBorder}
`;

const HeaderContainer = styled.div<{ $reserveMenuSpace?: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: flex-start;
  justify-content: space-between;
  padding-right: ${({ $reserveMenuSpace }) =>
    $reserveMenuSpace ? '2.5rem' : '0'};
`;

const NameAndAnalyticsIndicatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;

  & > button {
    flex-shrink: 0;
  }
`;

const MenuContainer = styled.div`
  position: absolute;
  top: 0.4rem;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
`;

const StyledLink = styled(Link)`
  ${bodyMedium};
  color: ${({ theme }) => theme.colors.gray333};
`;

const Description = styled.div`
  ${bodySmall};
  margin-top: 0.75rem;
  color: ${({ theme }) => theme.colors.gray666};
  white-space: pre-wrap;
`;

interface WorkflowCardProps {
  workflowRef: WorkflowRefResponse;
  onDeleteSuccess?: () => void;
}

export function WorkflowCard({
  workflowRef,
  onDeleteSuccess,
}: WorkflowCardProps) {
  const canEditWorkflow = useHasOrgUpdatePermissionOrSuperAdmin(
    workflowRef.organizationId,
  );
  const navigate = useNavigate();
  const { confirm } = useModal();
  const { showUserFriendlyErrorToast, success: showSuccess } = useToast();
  const [deleteWorkflow] = useWorkflowDelete({
    config: {
      onError: (err) =>
        showUserFriendlyErrorToast(
          err,
          `Unable to delete ${workflowRef.name} Workflow.`,
        ),
      onSuccess: () => {
        onDeleteSuccess && onDeleteSuccess();
        showSuccess(`${workflowRef.name} Workflow has been deleted.`);
      },
    },
  });

  const { workflowIdsSyncingToAnalytics = [] } = useWorkflowsSyncingToAnalytics(
    {
      organizationId: workflowRef.organizationId,
    },
  );

  const handleDelete = useCallback(async () => {
    await confirm({
      title: 'Delete Workflow',
      description: (
        <>
          Are you sure you want to delete the <b>{workflowRef.name}</b>{' '}
          Workflow?
        </>
      ),
      cancelButtonText: 'Keep Workflow',
      confirmButtonText: 'Delete Workflow',
      afterConfirmButtonText: 'Deleting...',
      danger: true,
      onConfirmAsync: () =>
        deleteWorkflow({
          organizationId: workflowRef.organizationId,
          workflowId: workflowRef.id,
        }),
    });
  }, [
    confirm,
    deleteWorkflow,
    workflowRef.id,
    workflowRef.name,
    workflowRef.organizationId,
  ]);

  const actionMenuOptions = useMemo(
    () => [
      {
        displayName: 'Edit',
        onClick: () => {
          navigate(`${workflowRef.id}/${RouteEditMode.EDIT}`);
        },
      },
      {
        displayName: 'Delete',
        onClick: handleDelete,
      },
    ],
    [handleDelete, navigate, workflowRef.id],
  );

  return (
    <Container>
      <HeaderContainer $reserveMenuSpace={canEditWorkflow}>
        <NameAndAnalyticsIndicatorContainer>
          <StyledLink to={`${workflowRef.id}`}>{workflowRef.name}</StyledLink>
          {workflowIdsSyncingToAnalytics.includes(workflowRef.id) && (
            <SyncingToAnalyticsIndicator />
          )}
        </NameAndAnalyticsIndicatorContainer>
        {workflowRef.doesItOrUpstreamUseIncompleteData && (
          <CalculationErrorChip label="Calculation error" />
        )}
      </HeaderContainer>
      {workflowRef.description && (
        <Description>{workflowRef.description}</Description>
      )}
      <WorkflowStepsDiagram
        organizationId={workflowRef.organizationId}
        workflowId={workflowRef.id}
      />
      {canEditWorkflow && (
        <MenuContainer>
          <ActionMenu
            altText={`Action menu for ${workflowRef.name} Workflow`}
            options={actionMenuOptions}
          />
        </MenuContainer>
      )}
    </Container>
  );
}
